import React, { memo, useCallback, useContext, useMemo, useState } from "react";

// core-ui components
import { HEADER_LOTTIE_ASSETS, LottieAnimation, withStyles } from "@swiggy-private/core-ui";

// styles
import styles from "../Header.scss";

// styled component
import { ExpressContainer } from "./styled";

// constants
import { ExpressLottieTransitionFrame, lottieProps, NonExpressLottieTransitionFrame } from "./constants";

// contexts
import { CartHeaderContext, INITIAL_CART_HEADER_CONTEXT_VALUE } from "@cart-client/contexts/CartHeaderContext";
import { CART_HEADER_DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";

// utils
import { getCloudinaryUrl } from "@swiggy-private/js-utils";
import { CartOverlaysContext } from "@swiggy-private/checkout-core";
import { LottieIds } from "@swiggy-private/cart-types";

const ExpressLottie = ({
    showExpressLottie,
    setExpressLottieShown,
}: {
    showExpressLottie: boolean;
    setExpressLottieShown: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const [hideLottie, setHideLottie] = useState(false);
    const { cartHeaderState, setCartHeaderState } = useContext(CartHeaderContext);
    const { onCloseOverlay, enabledOverlayId } = useContext(CartOverlaysContext);
    const { isOtg, isExpress } = cartHeaderState;

    const onLottieCompletion = useCallback(() => {
        setHideLottie(true);
        setExpressLottieShown(true);

        if (!isOtg && enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
            onCloseOverlay();
        }
    }, [isOtg, onCloseOverlay, enabledOverlayId, setExpressLottieShown]);

    const breakFramePoint = isExpress ? ExpressLottieTransitionFrame : NonExpressLottieTransitionFrame;

    const handleInitiateTheme = useCallback(() => {
        setCartHeaderState(prev => {
            if (!prev.hideTheme) {
                return { ...prev, themeChange: true };
            }
            return prev;
        });
    }, [setCartHeaderState]);

    const handleError = useCallback(() => {
        setCartHeaderState({
            ...INITIAL_CART_HEADER_CONTEXT_VALUE.cartHeaderState,
            hideTheme: true,
        });
        setHideLottie(true);
    }, [setCartHeaderState]);

    const lottiePath = useMemo(() => {
        return getCloudinaryUrl(
            isExpress ? HEADER_LOTTIE_ASSETS.EXPRESS_TRANSITION : HEADER_LOTTIE_ASSETS.NON_EXPRESS_TRANSITION,
        );
    }, [isExpress]);

    if (hideLottie) {
        return null;
    }

    return (
        <ExpressContainer
            data-testid={
                isExpress ? CART_HEADER_DATA_TEST_IDS.EXPRESS_LOTTIE : CART_HEADER_DATA_TEST_IDS.NON_EXPRESS_LOTTIE
            }
        >
            <LottieAnimation
                {...lottieProps}
                fileName={lottiePath}
                visible={showExpressLottie}
                playLottie={showExpressLottie}
                autoplay={showExpressLottie}
                className={styles.lottie}
                onPlayComplete={onLottieCompletion}
                frameEventBreak={breakFramePoint}
                onFrameEventCompletion={handleInitiateTheme}
                enableFrameEvent={true}
                preserveAspect="xMinYMid slice"
                onError={handleError}
            />
        </ExpressContainer>
    );
};

export default memo(withStyles(ExpressLottie, styles));
